/**
 * TRUS-703 (Integrated Inbox, phase 2): everything sent to or exchanged with a
 * member, from any path, is mirrored into that member's DM. Those rows carry a
 * small card in `metadata.card`:
 *
 *   campaign  {type, campaign_id, campaign_name, delivery_channel: sms|email, subject}
 *   call      {type, direction: inbound|outbound, status, duration_seconds, staff_user_id}
 *   voicemail {type, duration_seconds, transcript (staff only), recording_available,
 *              transcript_status: pending|done|failed|null (staff only, TRUS-719)}
 *
 * and a mirrored outbound SMS / email carries `metadata.mirrored = true` plus
 * `metadata.source` ("member_profile", "outreach", …).
 *
 * The backend ships alongside this, so every reader here is tolerant: a missing
 * or malformed field is dropped, an unknown `card.type` is null, and the caller
 * then renders the message as a plain bubble. The TRUS-668 in-app call message
 * (`metadata.call`) is a different shape and is never read here.
 */

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

export interface CampaignCard {
  type: 'campaign';
  /** campaigns.id is a uuid, so the server sends a string. */
  campaign_id: string | null;
  campaign_name: string | null;
  delivery_channel: 'sms' | 'email' | null;
  subject: string | null;
}

export type PhoneCallStatus = 'completed' | 'missed' | 'no_answer' | 'busy' | 'failed';

export interface PhoneCallCard {
  type: 'call';
  direction: 'inbound' | 'outbound' | null;
  status: PhoneCallStatus | null;
  duration_seconds: number | null;
  staff_user_id: number | null;
  /** Not in the contract; used if a server ever sends it. */
  staff_name: string | null;
}

export type TranscriptStatus = 'pending' | 'done' | 'failed';

export interface VoicemailCard {
  type: 'voicemail';
  duration_seconds: number | null;
  /** Staff only — the server strips it from member reads. */
  transcript: string | null;
  /** TRUS-719, staff only: transcription progress (null on older rows / servers). */
  transcript_status: TranscriptStatus | null;
  recording_available: boolean;
}

export type MemberDmCard = CampaignCard | PhoneCallCard | VoicemailCard;

const CALL_STATUSES = new Set<PhoneCallStatus>(['completed', 'missed', 'no_answer', 'busy', 'failed']);

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function idText(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function posInt(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function seconds(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/** The card on a mirrored member-DM message, or null (unknown / absent → plain bubble). */
export function memberDmCardOf(metadata: Record<string, any> | null | undefined): MemberDmCard | null {
  const c = metadata?.card;
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const type = typeof c.type === 'string' ? c.type.trim().toLowerCase() : '';
  switch (type) {
    case 'campaign': {
      const dc = typeof c.delivery_channel === 'string' ? c.delivery_channel.toLowerCase() : '';
      return {
        type: 'campaign',
        campaign_id: idText(c.campaign_id),
        campaign_name: str(c.campaign_name),
        delivery_channel: dc === 'sms' || dc === 'email' ? dc : null,
        subject: str(c.subject),
      };
    }
    case 'call': {
      const dir = c.direction === 'inbound' || c.direction === 'outbound' ? c.direction : null;
      return {
        type: 'call',
        direction: dir,
        status: CALL_STATUSES.has(c.status) ? c.status : null,
        duration_seconds: seconds(c.duration_seconds),
        staff_user_id: posInt(c.staff_user_id),
        staff_name: str(c.staff_name),
      };
    }
    case 'voicemail':
      return {
        type: 'voicemail',
        duration_seconds: seconds(c.duration_seconds),
        transcript: str(c.transcript),
        transcript_status:
          c.transcript_status === 'pending' || c.transcript_status === 'done' || c.transcript_status === 'failed'
            ? c.transcript_status
            : null,
        recording_available: c.recording_available === true,
      };
    default:
      return null;
  }
}

/** `metadata.source` of a mirrored outbound SMS / email, or null when the row is not mirrored. */
export function mirroredSourceOf(metadata: Record<string, any> | null | undefined): string | null {
  if (!metadata || metadata.mirrored !== true) return null;
  return str(metadata.source)?.trim().toLowerCase() ?? null;
}

/**
 * Card rows normally say where they came from by themselves (a campaign card
 * names its campaign), so they get no caption — except a bulk robocall
 * (TRUS-719), whose call card looks like any other outbound call. Its source
 * is read with or without `metadata.mirrored`.
 */
const CARD_CAPTION_SOURCES = new Set(['robocall']);

export function cardCaptionSourceOf(metadata: Record<string, any> | null | undefined): string | null {
  const source = str(metadata?.source)?.trim().toLowerCase() ?? null;
  return source && CARD_CAPTION_SOURCES.has(source) ? source : null;
}

/** "Sent from member profile" — known sources are translated, others humanised. */
export function mirroredSourceText(source: string, t: TFn): string {
  let where: string;
  switch (source) {
    case 'member_profile':
      where = t('messaging.mirror.source.memberProfile', 'member profile');
      break;
    case 'send_direct':
      where = t('messaging.mirror.source.sendDirect', 'direct send');
      break;
    case 'outreach':
      where = t('messaging.mirror.source.outreach', 'outreach');
      break;
    case 'workflow':
    case 'automation':
      where = t('messaging.mirror.source.workflow', 'a workflow');
      break;
    case 'mailbox':
      where = t('messaging.mirror.source.mailbox', 'Outlook');
      break;
    case 'bulk':
      where = t('messaging.mirror.source.bulk', 'a bulk send');
      break;
    case 'conversations':
      where = t('messaging.mirror.source.conversations', 'conversations');
      break;
    case 'campaign':
    case 'campaigns':
      where = t('messaging.mirror.source.campaign', 'a campaign');
      break;
    case 'ivr':
      where = t('messaging.mirror.source.ivr', 'the phone menu');
      break;
    // TRUS-719: more outbound sources mirrored into the DM.
    case 'meeting_invite':
      where = t('messaging.mirror.source.meetingInvite', 'a meeting invite');
      break;
    case 'event_invite':
      where = t('messaging.mirror.source.eventInvite', 'an event invite');
      break;
    case 'emergency':
      where = t('messaging.mirror.source.emergency', 'an emergency alert');
      break;
    case 'esignature':
    case 'e_signature':
      where = t('messaging.mirror.source.esignature', 'a signature request');
      break;
    case 'robocall':
      where = t('messaging.mirror.source.robocall', 'an automated call');
      break;
    default:
      where = source.replace(/[_-]+/g, ' ');
  }
  return t('messaging.mirror.sentFrom', 'Sent from {{source}}', { source: where });
}

/** First non-empty line of a message body (the collapsed campaign preview). */
export function firstLine(body: string | null | undefined): string {
  if (!body) return '';
  for (const line of body.split(/\r?\n/)) {
    if (line.trim()) return line.trim();
  }
  return '';
}
